import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "../../components/ui";
import { ErrorState, Skeleton } from "../../components/States";
import { staffApi, staffKeys, type CapacityResource } from "./api";
import "./staff.css";

const STATE: Record<string, { label: string; badge: string }> = {
  running: { label: "Running", badge: "ok" },
  paused: { label: "Paused", badge: "neutral" },
  asleep: { label: "Asleep", badge: "neutral" },
  unavailable: { label: "Unavailable", badge: "warn" },
};

function Card({ resource }: { resource: CapacityResource }) {
  const state = STATE[resource.state] ?? { label: resource.state, badge: "neutral" };
  return (
    <section className="card cap-card" aria-label={resource.name}>
      <header>
        <h2>{resource.name}</h2>
        <span className={`badge ${state.badge}`}>{state.label}</span>
      </header>
      {resource.detail ? <p className="muted">{resource.detail}</p> : null}
      {resource.items.length > 0 ? (
        <div className="sys-stats">
          {resource.items.map((item) => (
            <div className="sys-stat" key={item.label}>
              <span className="label">{item.label}</span>
              <span className="value text">{item.value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

export function StaffCapacityPage() {
  const report = useQuery({
    queryKey: staffKeys.capacity,
    queryFn: staffApi.capacity,
    refetchInterval: 60_000,
  });

  return (
    <div>
      <PageHeader
        title="Capacity"
        subtitle="Current size and load. Reading this does not wake a paused database or analytics."
        actions={
          <button className="btn ghost" type="button" onClick={() => void report.refetch()} disabled={report.isFetching}>
            {report.isFetching && report.data ? "Refreshing…" : "Refresh"}
          </button>
        }
      />
      {report.isLoading ? <Skeleton lines={8} /> : null}
      {report.isError ? <ErrorState error={report.error} retry={() => void report.refetch()} /> : null}
      {report.data ? (
        <div className="cap-grid">
          <Card resource={report.data.machines} />
          <Card resource={report.data.database} />
          <Card resource={report.data.analytics} />
          <Card resource={report.data.recordings} />
          {report.data.costs ? <Card resource={report.data.costs} /> : null}
        </div>
      ) : null}
    </div>
  );
}
